import { useState } from "react";
import { useSelector } from "react-redux";
import { WalletFieldConfig } from "@evergiving/common/dist/es2015/form";
import {
  PaymentFrequency,
  PaymentSourceKind,
} from "@evergiving/common/dist/es2015/pledge";
import { amountSelector } from "@/lib/misc/amountSelector";
import { frequencySelector } from "@/lib/misc/frequencySelector";
import { recurringPaymentRequestFor } from "@/lib/misc/applePayRecurring";
import { StripePaymentContext } from "@/lib/hooks/usePaymentCommon";
import { WalletKind, walletKindFor } from "@/lib/misc/walletKind";

import {
  Elements,
  ExpressCheckoutElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import {
  loadStripe,
  Stripe,
  StripeExpressCheckoutElementConfirmEvent,
} from "@stripe/stripe-js";

/**
 * What a submission tells the wallet sheet. Stripe keeps the sheet open until
 * the payment is confirmed or reported failed, so every path must answer.
 */
export interface StripeCheckoutOutcome {
  failed: boolean;
  message?: string;
}

const stripePromises = new Map<string, Promise<Stripe | null>>();

/**
 * One Stripe instance per publishable key. Elements ignores a stripe prop that
 * changes after mounting, so a fresh promise on every render is wasted work at
 * best.
 */
export const stripePromiseFor = (
  publishableKey: string
): Promise<Stripe | null> => {
  const existing = stripePromises.get(publishableKey);
  if (existing) {
    return existing;
  }

  const created = loadStripe(publishableKey);
  stripePromises.set(publishableKey, created);
  return created;
};

const WalletButtons = ({
  charityName,
  config,
  kind,
  onSubmit,
}: {
  charityName: string;
  config: WalletFieldConfig;
  kind: WalletKind;
  onSubmit: (
    stripePaymentContext?: StripePaymentContext
  ) => Promise<StripeCheckoutOutcome>;
}) => {
  const stripeInstance = useStripe();
  const paymentElements = useElements();
  const [walletUnavailable, setWalletUnavailable] = useState(false);
  const amount = useSelector(amountSelector);
  const frequency = useSelector(frequencySelector);

  // Only Apple reads this. Stripe ignores it on the Google Pay button, and the
  // element draws one wallet at a time anyway.
  const recurringPaymentRequest = recurringPaymentRequestFor({
    frequency,
    amount,
    charityName,
  });

  if (paymentElements == null || stripeInstance == null) {
    return <>{config.noWalletAvailableText}</>;
  }

  const handleCheckout = async (
    event: StripeExpressCheckoutElementConfirmEvent
  ) => {
    // Only one wallet is drawn, so anything else means Stripe offered a button
    // this form did not ask for. Submitting it would name a payment source the
    // link may not authorise, so fail rather than guess.
    const walletKind = walletKindFor(event.expressPaymentType);

    if (walletKind !== kind) {
      event.paymentFailed({ reason: "fail" });
      return;
    }

    try {
      // Trigger form validation and wallet collection
      const submit = await paymentElements.submit();
      if (submit.error) {
        event.paymentFailed({ reason: "fail", message: submit.error.message });
        return;
      }

      const outcome = await onSubmit({
        paymentElements,
        stripeInstance,
        walletKind,
      });
      if (outcome.failed) {
        event.paymentFailed({ reason: "fail", message: outcome.message });
      }
    } catch {
      event.paymentFailed({
        reason: "fail",
        message: "Unable to complete payment.",
      });
    }
  };

  const checkoutElement = (
    // WalletButtons is keyed on the wallet, so switching remounts the element.
    // react-stripe-js creates the Stripe element once - its creation effect is
    // guarded on `elementRef.current === null` - and `paymentMethods` is not
    // in StripeExpressCheckoutElementUpdateOptions, so it is dropped from any
    // update. Without the key the first wallet's buttons stay on screen and
    // the second wallet's never appear.
    <ExpressCheckoutElement
      onConfirm={handleCheckout}
      onReady={({ availablePaymentMethods }) =>
        setWalletUnavailable(!availablePaymentMethods?.[kind])
      }
      onLoadError={() => setWalletUnavailable(true)}
      options={{
        paymentMethods: {
          applePay: kind === PaymentSourceKind.ApplePay ? "always" : "never",
          googlePay: kind === PaymentSourceKind.GooglePay ? "always" : "never",
          amazonPay: "never",
          link: "never",
          paypal: "never",
        },
        buttonType: { googlePay: "donate", applePay: "donate" },
        layout: { maxColumns: 1, maxRows: 2 },
        ...(recurringPaymentRequest && {
          applePay: { recurringPaymentRequest },
        }),
      }}
    />
  );

  return (
    <>
      {checkoutElement}
      {walletUnavailable && (
        <div role="status">{config.noWalletAvailableText}</div>
      )}
    </>
  );
};

export const SubmitWithStripeButton = ({
  canSubmit,
  charityName,
  onSubmit,
  config,
  kind,
}: {
  canSubmit: boolean;
  charityName: string;
  onSubmit: (
    stripePaymentContext?: StripePaymentContext
  ) => Promise<StripeCheckoutOutcome>;
  config: WalletFieldConfig;
  kind: WalletKind;
}) => {
  const amount = useSelector(amountSelector);
  const frequency = useSelector(frequencySelector);
  const setupFutureUsage =
    frequency !== undefined && frequency !== PaymentFrequency.OneTime
      ? "off_session"
      : null;
  const stripePromise = stripePromiseFor(config.publishableKey);
  const disabled = !canSubmit ? "disabled" : "";
  const className = `wallet ${disabled}`;

  return (
    <div className={className}>
      <Elements
        stripe={stripePromise}
        options={{
          mode: "payment",
          amount: amount.value,
          currency: amount.currency.toLowerCase(),
          setupFutureUsage,
        }}
      >
        <WalletButtons
          key={kind}
          charityName={charityName}
          config={config}
          kind={kind}
          onSubmit={onSubmit}
        />
      </Elements>
    </div>
  );
};
